import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Mail, Phone, MapPin, Search, Inbox, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { IMAGES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(data);
    } catch {
      toast.error("Kunde inte hämta förfrågningar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return quotes;
    return quotes.filter((x) =>
      [x.name, x.email, x.phone, x.address, x.service_type, x.message].join(" ").toLowerCase().includes(s)
    );
  }, [quotes, q]);

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-brand-tint" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="Se Klart Ut" className="h-9 w-auto object-contain" />
            <span className="text-sm text-slate-400 hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:inline">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-slate-200 text-brand-navy text-sm font-medium px-4 py-2 hover:border-brand-accent hover:text-brand-accent transition-colors">
              <LogOut className="h-4 w-4" /> Logga ut
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-brand-navy">Offertförfrågningar</h1>
            <p className="mt-1 text-slate-500">{quotes.length} totalt · visar {filtered.length}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input" placeholder="Sök namn, e-post, tjänst…" className="rounded-full bg-white border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:ring-2 focus:ring-brand-accent focus:outline-none w-64" />
            </div>
            <button onClick={load} data-testid="admin-refresh-button" className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:border-brand-accent transition-colors" aria-label="Uppdatera">
              <RefreshCw className={`h-4 w-4 text-brand-navy ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="text-center py-20 text-slate-400">Laddar…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
              <Inbox className="h-10 w-10 text-slate-300 mx-auto" />
              <p className="mt-3 text-slate-500">Inga förfrågningar ännu.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filtered.map((item) => (
                <div key={item.id} data-testid="admin-quote-card" className="bg-white rounded-2xl p-6 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${item.customer_type === "foretag" ? "bg-brand-navy/10 text-brand-navy" : "bg-brand-accent/10 text-brand-accent"}`}>
                        {item.customer_type === "foretag" ? "Företag" : "Privat"}
                      </span>
                      <h3 className="text-lg font-semibold text-brand-navy">{item.name}</h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-400">{fmt(item.created_at)}</span>
                      {item.email_sent
                        ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600" title="E-post skickad"><CheckCircle2 className="h-4 w-4" /> Mailad</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-slate-400" title="Ej mailad (endast sparad)"><XCircle className="h-4 w-4" /> Ej mailad</span>}
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-slate-600 hover:text-brand-accent"><Mail className="h-4 w-4 text-brand-accent" /> {item.email}</a>
                    <a href={`tel:${item.phone}`} className="flex items-center gap-2 text-slate-600 hover:text-brand-accent"><Phone className="h-4 w-4 text-brand-accent" /> {item.phone}</a>
                    {item.address ? <span className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-brand-accent" /> {item.address}</span> : <span />}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    {(item.services && item.services.length > 0) ? (
                      <div className="w-full">
                        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Tjänster</p>
                        <div className="flex flex-wrap gap-2">
                          {item.services.map((s) => (
                            <span key={s} className="inline-flex items-center gap-1.5 text-xs font-medium bg-brand-accent/10 text-brand-accent px-2.5 py-1 rounded-full">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              {({fonsterputs:"Fönsterputs",solpaneltvatt:"Solpaneltvätt",altantvatt:"Altantvätt",marktvatt:"Marktvätt",annat:"Annat"})[s] || s}
                              {s === "fonsterputs" && item.fonsterputs_level ? ` – ${item.fonsterputs_level}` : ""}
                              {s === "fonsterputs" && item.mellan_fonstren ? " + Mellan fönstren" : ""}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : item.service_type ? (
                      <span className="text-slate-500">Tjänst: <span className="text-brand-navy font-medium">{item.service_type}</span></span>
                    ) : null}
                  </div>
                  {(item.num_windows || item.property_type || item.num_solar_panels || item.altan_info || item.mark_info || item.annat_info) && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm bg-slate-50 rounded-xl p-4">
                      {item.num_windows && <div><span className="text-slate-500">Antal fönster:</span> <span className="text-brand-navy font-medium">{item.num_windows}</span></div>}
                      {item.property_type && <div><span className="text-slate-500">Fastighet:</span> <span className="text-brand-navy font-medium">{item.property_type}</span></div>}
                      {item.num_solar_panels && <div><span className="text-slate-500">Solpaneler:</span> <span className="text-brand-navy font-medium">{item.num_solar_panels}</span></div>}
                      {item.altan_info && <div><span className="text-slate-500">Altan:</span> <span className="text-brand-navy font-medium">{item.altan_info}</span></div>}
                      {item.mark_info && <div><span className="text-slate-500">Marktvätt:</span> <span className="text-brand-navy font-medium">{item.mark_info}</span></div>}
                      {item.annat_info && <div className="sm:col-span-2"><span className="text-slate-500">Annat:</span> <span className="text-brand-navy font-medium">{item.annat_info}</span></div>}
                    </div>
                  )}
                  {item.message && <p className="mt-3 text-slate-600 bg-slate-50 rounded-xl p-4 text-sm">{item.message}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
